import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  ClockIcon,
  ExpandMoreIcon,
  LocationIcon,
  PhoneIcon,
  ProfileIcon,
  ServiceCenterIcon,
  StoreIcon,
} from 'assets/icons';
import MasterFilter, {
  type FilterFieldConfig,
  type FilterOption,
  useMasterFilter,
} from 'components/common/MasterFilter';
import PillTabs from 'components/common/PillTabs';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import { type ComponentType, type FC, type ReactNode, useMemo, useState } from 'react';
import {
  type BranchOfficeDetail,
  useGetBusinessUnitAllowedBrandsQuery,
  useGetBusinessUnitBranchOfficeDetailsQuery,
  useGetBusinessUnitServiceCenterDetailsQuery,
} from 'services/api/businessUnits.api';
import { NAVY, RADIUS_CARD, SHADOW, SURFACE, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

const FONT = 'Jost, sans-serif';
const ALLOWED_TYPES = ['car_dealership', 'after_sales'];

type SedeView = 'sucursales' | 'talleres';
type IconType = ComponentType<{ sx?: object }>;

const InfoLine: FC<{ Icon: IconType; children: ReactNode }> = ({ Icon, children }) => (
  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
    <Icon sx={{ fontSize: 18, color: alpha(NAVY, 0.45), mt: '2px', flexShrink: 0 }} />
    <Box sx={{ fontFamily: FONT, fontSize: '0.85rem', color: alpha(NAVY, 0.75), minWidth: 0 }}>{children}</Box>
  </Box>
);

const SedeRow: FC<{ Icon: IconType; office: BranchOfficeDetail }> = ({ Icon, office }) => {
  const [open, setOpen] = useState(false);
  const advisorLabel = [office.advisorName, office.advisorRole].filter(Boolean).join(' · ');

  return (
    <Box sx={{ bgcolor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_CARD, boxShadow: SHADOW, overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2.5,
          py: 1.75,
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
          '&:hover': { bgcolor: alpha(NAVY, 0.03) },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: alpha(NAVY, 0.06),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 20, color: NAVY }} />
        </Box>
        <Typography
          sx={{ flexGrow: 1, minWidth: 0, fontFamily: FONT, fontWeight: 500, fontSize: '0.95rem', color: NAVY }}
        >
          {office.name}
        </Typography>
        <ExpandMoreIcon
          sx={{
            color: alpha(NAVY, 0.5),
            transition: 'transform 0.2s ease',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </Box>

      <Collapse in={open} unmountOnExit>
        <Stack spacing={1} sx={{ pl: 7, pr: 2.5, pb: 2.25 }}>
          <InfoLine Icon={LocationIcon}>
            {office.mapsUrl ? (
              <Link
                href={office.mapsUrl}
                target="_blank"
                rel="noopener"
                sx={{ color: NAVY, textDecorationColor: alpha(NAVY, 0.3) }}
              >
                {office.address}
              </Link>
            ) : (
              office.address
            )}
          </InfoLine>

          {office.hours.length > 0 && (
            <InfoLine Icon={ClockIcon}>
              {office.hours.map((line) => (
                <Box key={line}>{line}</Box>
              ))}
            </InfoLine>
          )}

          {office.phone && (
            <InfoLine Icon={PhoneIcon}>
              <Link href={`tel:${office.phone}`} sx={{ color: NAVY, textDecorationColor: alpha(NAVY, 0.3) }}>
                {office.phone}
              </Link>
            </InfoLine>
          )}

          {advisorLabel && (
            <InfoLine Icon={ProfileIcon}>
              {advisorLabel}
              {office.advisorEmail && (
                <Box>
                  <Link
                    href={`mailto:${office.advisorEmail}`}
                    sx={{ color: NAVY, textDecorationColor: alpha(NAVY, 0.3) }}
                  >
                    {office.advisorEmail}
                  </Link>
                </Box>
              )}
            </InfoLine>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

const CenteredPanel: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '50vh',
      backgroundColor: SURFACE_CONTAINER_LOW,
      borderRadius: RADIUS_CARD,
      p: 3,
      textAlign: 'center',
    }}
  >
    <Typography variant="h6" sx={{ fontFamily: FONT, color: NAVY, opacity: 0.5 }}>
      {children}
    </Typography>
  </Box>
);

const BranchOffices: FC = () => {
  const { selectedBusinessUnitId } = useSelectedBusinessUnit();
  const { businessUnits } = useBusinessUnits();
  const businessUnit = businessUnits?.find((bu) => bu.id === selectedBusinessUnitId);
  const businessUnitId = selectedBusinessUnitId ?? -1;

  const typeAllowed = businessUnit ? ALLOWED_TYPES.includes(businessUnit.type ?? '') : true;
  const skip = businessUnitId === -1 || !typeAllowed;

  const filterConfig = useMemo<FilterFieldConfig[]>(
    () => [{ key: 'brands', kind: 'multiSelect', label: 'Marca', placement: 'bar' }],
    []
  );
  const filter = useMasterFilter(filterConfig, skip ? null : businessUnitId);
  const selectedBrands = (filter.values.brands as string[] | undefined) ?? [];
  const brandsArg = selectedBrands.length > 0 ? selectedBrands : undefined;

  const { data: brands = [] } = useGetBusinessUnitAllowedBrandsQuery({ businessUnitId }, { skip });
  const { data: offices = [], isLoading: loadingOffices } = useGetBusinessUnitBranchOfficeDetailsQuery(
    { businessUnitId, brands: brandsArg },
    { skip }
  );
  const { data: centers = [], isLoading: loadingCenters } = useGetBusinessUnitServiceCenterDetailsQuery(
    { businessUnitId, brands: brandsArg },
    { skip }
  );

  const brandOptions = useMemo<FilterOption[]>(() => brands.map((brand) => ({ value: brand, label: brand })), [brands]);
  const dynamicOptions = useMemo(() => ({ brands: brandOptions }), [brandOptions]);

  const [view, setView] = useState<SedeView>('sucursales');

  const wrap = (content: ReactNode) => (
    <Box sx={{ backgroundColor: SURFACE, minHeight: '100vh', p: { xs: 2, md: 4 } }}>{content}</Box>
  );

  if (businessUnitId === -1) {
    return wrap(<CenteredPanel>Selecciona una unidad de negocio para ver sus sucursales</CenteredPanel>);
  }

  if (businessUnit && !typeAllowed) {
    return wrap(<CenteredPanel>Esta vista solo está disponible para concesionarios y postventa</CenteredPanel>);
  }

  const isLoading = loadingOffices || loadingCenters;
  const hasOffices = offices.length > 0;
  const hasCenters = centers.length > 0;
  const showTabs = hasOffices && hasCenters;
  const activeView: SedeView = showTabs ? view : hasCenters && !hasOffices ? 'talleres' : 'sucursales';
  const activeCount = activeView === 'sucursales' ? offices.length : centers.length;

  return wrap(
    <>
      {brands.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <MasterFilter {...filter} dynamicOptions={dynamicOptions} />
        </Box>
      )}

      {(hasOffices || hasCenters) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2,
            minHeight: 40,
          }}
        >
          <Typography variant="caption" sx={{ fontFamily: FONT, color: 'text.secondary' }}>
            {activeCount} {activeView === 'sucursales' ? 'sucursales' : 'talleres'}
          </Typography>
          {showTabs && (
            <PillTabs<SedeView>
              ariaLabel="Tipo de sede"
              tabs={[
                { value: 'sucursales', label: 'Sucursales' },
                { value: 'talleres', label: 'Talleres' },
              ]}
              value={view}
              onChange={setView}
            />
          )}
        </Box>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: alpha(NAVY, 0.3) }} />
        </Box>
      ) : activeView === 'sucursales' ? (
        hasOffices ? (
          <Stack spacing={1.5}>
            {offices.map((office) => (
              <SedeRow key={office.name} Icon={StoreIcon} office={office} />
            ))}
          </Stack>
        ) : (
          <Typography sx={{ fontFamily: FONT, color: alpha(NAVY, 0.4), py: 4 }}>
            No hay sucursales disponibles
          </Typography>
        )
      ) : hasCenters ? (
        <Stack spacing={1.5}>
          {centers.map((center) => (
            <SedeRow key={center.name} Icon={ServiceCenterIcon} office={center} />
          ))}
        </Stack>
      ) : (
        <Typography sx={{ fontFamily: FONT, color: alpha(NAVY, 0.4), py: 4 }}>No hay talleres disponibles</Typography>
      )}
    </>
  );
};

export default BranchOffices;
